import React, { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';

import type { WorkSimpleForExportData } from '../../shared/dto/EntityRepository';
import { WORK_LIST_EXPORT_MAX_ROWS } from '../../shared/constants/limits';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { useScrollContainerMarqueeSelect } from '../../shared/hooks/useScrollContainerMarqueeSelect';
import { useExportStationWorkStore } from '../../shared/store/exportStationWorkStore';
import { useModalWorkTagStore } from '../../shared/store/workTagStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';

import ImportTagStation from '../projectDetail/ImportTagStation';
import PageAboutWorkSelect from './PageAboutWorkSelect';
import SimpleWorkForExport, { simpleWorkRowToPdfExportPayload } from './SimpleWorkForExport';

const getExportWorkItemId = (row: WorkSimpleForExportData) => String(row.id);

const WorkListPdfModalSelect: React.FC = () => {
  const { simpleList } = useExportStationWorkStore();
  const { selectedModalTags } = useModalWorkTagStore();
  const { enqueueAlert } = useStandardAlertStore();
  const listRef = useRef<HTMLDivElement>(null);

  const commitMarquee = useCallback(
    (hit: WorkSimpleForExportData[]) => {
      if (hit.length === 0) return;
      let showedAlert = false;
      for (const row of hit) {
        const { selectedDataList, addSelectedData } = useExportStationWorkStore.getState();
        const taken = selectedDataList.some(
          (s) => String(s.workId) === String(row.id) && s.detailId === undefined
        );
        if (taken) continue;
        if (selectedDataList.length >= WORK_LIST_EXPORT_MAX_ROWS) {
          if (!showedAlert) {
            enqueueAlert({
              type: AlertType.ALERT,
              position: AlertPosition.TOP,
              content: getAlertMessage('WORK_LIST_EXPORT_MAX_ITEMS', {
                maxCount: WORK_LIST_EXPORT_MAX_ROWS,
              }),
            });
            showedAlert = true;
          }
          break;
        }
        addSelectedData(simpleWorkRowToPdfExportPayload(row));
      }
    },
    [enqueueAlert]
  );

  const {
    handleContainerMouseDown,
    handleContainerClickCapture,
    marqueeBox,
    marqueeSelecting,
    hitItems,
  } = useScrollContainerMarqueeSelect({
    items: simpleList,
    getItemId: getExportWorkItemId,
    containerRef: listRef,
    enabled: simpleList.length > 0,
    onCommit: commitMarquee,
  });

  const hitIdSet = useMemo(() => new Set(hitItems.map((w) => String(w.id))), [hitItems]);

  return (
    <SelectedDataContainerWrapper>
      <div data-suppress-marquee>
        <ImportTagStation />
      </div>
      <WorkListContainer
        ref={listRef}
        onMouseDown={handleContainerMouseDown}
        onClickCapture={handleContainerClickCapture}
        $marqueeSelecting={marqueeSelecting}
      >
        {simpleList.length === 0 ? (
          <NoContentContainer>
            {selectedModalTags.length > 0
              ? 'No works for selected tags'
              : 'No works'}
          </NoContentContainer>
        ) : (
          simpleList.map((sw) => (
            <SimpleContentComp key={sw.id}>
              <SimpleWorkForExport data={sw} isMarqueeHit={hitIdSet.has(String(sw.id))} />
            </SimpleContentComp>
          ))
        )}
        {marqueeBox ? (
          <MarqueeSelectionBox
            style={{
              left: marqueeBox.left,
              top: marqueeBox.top,
              width: marqueeBox.width,
              height: marqueeBox.height,
            }}
          />
        ) : null}
      </WorkListContainer>
      <PagePerContainer>
        <PageAboutWorkSelect />
      </PagePerContainer>
    </SelectedDataContainerWrapper>
  );
};

const SelectedDataContainerWrapper = styled.div`
  width: 100%;
  height: 75vh;
  flex: 4;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const WorkListContainer = styled.div<{ $marqueeSelecting?: boolean }>`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: scroll;
  position: relative;
  user-select: ${({ $marqueeSelecting }) => ($marqueeSelecting ? 'none' : 'auto')};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
  z-index: 2;
`;

const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_2};
  white-space: pre-line;
`;

const SimpleContentComp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const PagePerContainer = styled.div`
  position: absolute;
  bottom: -4vh;
  left: 0;
  width: 100%;
`;

export default WorkListPdfModalSelect;
